import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Paper, IconButton, Tooltip } from '@mui/material';
import { Close, DragIndicator, ContentCopy } from '@mui/icons-material';
import Editor from '@monaco-editor/react';
import { useTheme } from '@/hooks';

interface JsonDetailPanelProps {
  title: string;
  data: unknown;
  onClose: () => void;
  onCopySuccess?: (message: string) => void;
  onCopyError?: (message: string) => void;
}

const JsonDetailPanel: React.FC<JsonDetailPanelProps> = ({ 
  title, 
  data, 
  onClose, 
  onCopySuccess, 
  onCopyError 
}) => {
  const jsonString = JSON.stringify(data, null, 2);
  const { mode } = useTheme();
  
  // Helper function to copy JSON to clipboard
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(jsonString);
      if (onCopySuccess) {
        onCopySuccess('JSON copied to clipboard');
      }
    } catch (err) {
      console.error('Failed to copy JSON to clipboard:', err);
      if (onCopyError) {
        onCopyError('Failed to copy JSON to clipboard');
      }
    }
  };
  
  // Panel width state with localStorage persistence
  const [panelWidth, setPanelWidth] = useState(() => {
    const saved = localStorage.getItem('jsonDetailPanelWidth');
    return saved ? Number.parseInt(saved, 10) : 500; // Default to 500px (larger than previous 400px)
  });

  const [isResizing, setIsResizing] = useState(false);
  
  // Constants for constraints
  const MIN_WIDTH = 300;
  const MAX_WIDTH = 800;

  // Save to localStorage when width changes
  useEffect(() => {
    localStorage.setItem('jsonDetailPanelWidth', panelWidth.toString());
  }, [panelWidth]);

  // Handle mouse move during resize
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;
    
    const newWidth = window.innerWidth - e.clientX;
    const constrainedWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth));
    setPanelWidth(constrainedWidth);
  }, [isResizing]);

  // Handle mouse up to stop resizing
  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  // Set up global mouse events during resize
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  return (
    <Paper
      sx={{
        width: panelWidth,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 0,
        borderLeft: '1px solid',
        borderColor: 'divider',
        m: 0,
        position: 'relative',
      }}
    >
      {/* Resize Handle */}
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 8,
          cursor: 'col-resize',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: 'primary.main',
            opacity: 0.1,
          },
          transition: 'background-color 0.2s ease',
        }}
      >
        <DragIndicator 
          sx={{ 
            fontSize: 16, 
            color: 'grey.400',
            transform: 'rotate(90deg)',
            opacity: 0.7,
          }} 
        />
      </Box>

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 64, // Fixed height to match page title section
          px: 2,
          pl: 3, // Extra padding to account for resize handle
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: mode === 'dark' ? 'background.paper' : 'grey.50',
        }}
      >
        <Typography variant="h6" component="div">
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Copy JSON to clipboard">
            <IconButton onClick={copyToClipboard} size="small">
              <ContentCopy />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </Box>

      {/* JSON Editor */}
      <Box sx={{ flex: 1, minHeight: 0, pl: 1 }}> {/* Extra padding for resize handle */}
        <Editor
          height="100%"
          defaultLanguage="json"
          value={jsonString}
          loading={<div>Loading Monaco Editor...</div>}
          options={{
            readOnly: true,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            wordWrap: 'on',
            lineNumbers: 'on',
            folding: true,
            foldingStrategy: 'indentation',
            showFoldingControls: 'always',
            selectOnLineNumbers: true,
            automaticLayout: true,
            fontSize: 12,
          }}
          theme={mode === 'dark' ? 'vs-dark' : 'vs'}
        />
      </Box>
    </Paper>
  );
};

export default JsonDetailPanel;
